import { Link } from 'gatsby';
import type * as React from 'react';
import { useCallback, useState } from 'react';

import { useLanguage } from '../../context/LanguageContext';
import logo from '../../images/logo_full.png';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import MobileMenu from '../MobileMenu/MobileMenu';
import styles from './NavigationBar.module.css';

export interface NavigationBarProps {
  lang: 'de' | 'en';
  currentPath: string;
}

interface NavItem {
  labelKey: string;
  path: string;
}

const NAV_ITEMS_DE: NavItem[] = [
  { labelKey: 'nav.firm', path: '/kanzlei/' },
  { labelKey: 'nav.practiceAreas', path: '/fachgebiete/' },
  { labelKey: 'nav.team', path: '/team/' },
  { labelKey: 'nav.contact', path: '/kontakt/' },
];

const NAV_ITEMS_EN: NavItem[] = [
  { labelKey: 'nav.firm', path: '/en/the-firm/' },
  { labelKey: 'nav.practiceAreas', path: '/en/areas-of-expertise/' },
  { labelKey: 'nav.team', path: '/en/team/' },
  { labelKey: 'nav.contact', path: '/en/contact/' },
];

/**
 * Determines if a navigation link is active based on the current path.
 * A link is active if the current path starts with the link path,
 * handling both exact matches and sub-pages (e.g., /team/werner-minihold/).
 */
function isLinkActive(currentPath: string, linkPath: string): boolean {
  // Normalize paths to ensure trailing slash consistency
  const normalizedCurrent = currentPath.endsWith('/') ? currentPath : `${currentPath}/`;
  const normalizedLink = linkPath.endsWith('/') ? linkPath : `${linkPath}/`;

  return normalizedCurrent === normalizedLink || normalizedCurrent.startsWith(normalizedLink);
}

/**
 * NavigationBar component.
 *
 * Displays the "Minihold Rechtsanwälte" text logo, navigation links
 * (Kanzlei, Fachgebiete, Team, Kontakt or English equivalents),
 * a LanguageSwitcher, and a hamburger button for mobile viewports.
 *
 * Active state is applied to the link matching the current page.
 * All interactive elements are keyboard-navigable with visible focus indicators.
 */
const NavigationBar: React.FC<NavigationBarProps> = ({ lang, currentPath }) => {
  const { t, alternateSlug } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = lang === 'de' ? NAV_ITEMS_DE : NAV_ITEMS_EN;
  const homePath = lang === 'de' ? '/' : '/en/';

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  return (
    <>
      <nav
        className={styles.navbar}
        aria-label={lang === 'de' ? 'Hauptnavigation' : 'Main navigation'}
      >
        {/* Logo */}
        <Link to={homePath} className={styles.logo} aria-label="Minihold Rechtsanwälte - Home">
          <img src={logo} alt="Minihold Rechtsanwälte" className={styles.logoImage} />
        </Link>

        {/* Desktop navigation links */}
        <ul className={styles.navLinks}>
          {navItems.map((item) => {
            const active = isLinkActive(currentPath, item.path);
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`${styles.navLink}${active ? ` ${styles.navLinkActive}` : ''}`}
                  aria-current={active ? 'page' : undefined}
                >
                  {t(item.labelKey)}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Desktop language switcher */}
        <div className={styles.actions}>
          <LanguageSwitcher lang={lang} alternateSlug={alternateSlug} />
        </div>

        {/* Hamburger button (visible below 768px) */}
        <button
          type="button"
          className={styles.hamburger}
          onClick={toggleMobileMenu}
          aria-expanded={mobileMenuOpen}
          aria-controls="mobile-menu"
          aria-label={lang === 'de' ? 'Menü öffnen' : 'Open menu'}
        >
          <span className={styles.hamburgerIcon} aria-hidden="true" />
        </button>
      </nav>

      {/* Mobile menu */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={closeMobileMenu}
        lang={lang}
        currentPath={currentPath}
      />
    </>
  );
};

export default NavigationBar;
